/**
 * bw-acquisition.js
 * First-party acquisition ledger for Bridgewater Direct.
 *
 * First touch belongs to this browser. The cart receives that record.
 *
 * Cart lifecycle:
 *   A. Fresh visitor, empty cart — GET cart.js (empty cart still has a token).
 *   B. Phase A writes private __bw_* attributes onto that empty cart.
 *   C. Adding products keeps the same cart token; attributes remain.
 *   D. Checkout uses that cart; Order 1 carries the bound acquisition.
 *   E. After purchase Shopify issues a new cart token.
 *   F. A new cart token keeps a meaningful record whose own acquisition
 *      timestamp is under 14 days. chooseRecord decides: Direct / internal
 *      navigation cannot overwrite it, a new click id replaces it, and a
 *      GMC free-listing click replaces a non-GMC record. When the stored
 *      record wins, the cart is marked __bw_acq_carried.
 *   G. A meaningful record 14 days or older is not copied onto a new cart.
 *
 * Same-cart replacement (deterministic, unchanged):
 *   - Direct / internal navigation never overwrites a meaningful acquisition.
 *   - A new paid click ID (gclid / gbraid / wbraid) may replace.
 *   - A genuine GMC free-listing click may replace a non-GMC record.
 *   - Paid on the incoming page is classified before GMC / organic.
 *   - Carry age is the record's acquisition timestamp, not when it was saved.
 *   - Storage key stays bw_acq_v2 so an existing ledger is still readable.
 *
 * Classifier hierarchy (do not change):
 *   Google Paid → GMC Free Listing → Google Organic Search → Bing Organic
 *   → AI Assistant → Referral → Direct
 */
(function () {
  'use strict';

  var VERSION = '2';
  var STORAGE_KEY = 'bw_acq_v2';
  var LEGACY_KEYS = ['bw_acq_v1', 'bw_acq_v1_written'];
  var STALE_MS = 14 * 24 * 60 * 60 * 1000;
  var CHECKOUT_WAIT_MS = 1800;
  var REF_MAX = 180;
  var ID_MAX = 200;
  var TEXT_MAX = 180;
  var GA_SESSION_COOKIE = '_ga_9X115J0X65';
  var QUERY_ALLOWLIST = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'gclid',
    'gbraid',
    'wbraid',
    'srsltid',
    'variant'
  ];

  var CHANNELS = {
    PAID: 'Google Paid',
    GMC: 'GMC Free Listing',
    GOOGLE_ORG: 'Google Organic Search',
    BING_ORG: 'Bing Organic',
    AI: 'AI Assistant',
    REFERRAL: 'Referral',
    DIRECT: 'Direct'
  };

  var ATTR_KEYS = [
    ['channel', '__bw_acq_channel'],
    ['source', '__bw_acq_source'],
    ['medium', '__bw_acq_medium'],
    ['campaign', '__bw_acq_campaign'],
    ['content', '__bw_acq_content'],
    ['srsltid', '__bw_srsltid'],
    ['gclid', '__bw_gclid'],
    ['gbraid', '__bw_gbraid'],
    ['wbraid', '__bw_wbraid'],
    ['landing_path', '__bw_landing_path'],
    ['landing_query', '__bw_landing_query'],
    ['referrer', '__bw_referrer'],
    ['timestamp', '__bw_acq_timestamp'],
    ['carried', '__bw_acq_carried'],
    ['carried_at', '__bw_acq_carried_at'],
    ['version', '__bw_acq_version'],
    ['ga_client_id', '__bw_ga_client_id'],
    ['ga_session_id', '__bw_ga_session_id']
  ];

  var CORE_ATTR_KEYS = ATTR_KEYS.filter(function (pair) {
    return pair[0] !== 'ga_client_id' && pair[0] !== 'ga_session_id';
  });

  var AI_HOSTS = [
    'chatgpt.com',
    'chat.openai.com',
    'perplexity.ai',
    'claude.ai',
    'copilot.microsoft.com',
    'you.com',
    'phind.com',
    'grok.x.ai',
    'x.ai'
  ];

  var CHECKOUT_SELECTOR = [
    '[name="checkout"]',
    '[data-bw-checkout]',
    'shopify-accelerated-checkout',
    'shopify-accelerated-checkout-cart',
    '.shopify-payment-button',
    '.shopify-payment-button__button',
    'a[href*="/checkout"]'
  ].join(', ');

  var record = null;
  var cartToken = '';
  var lastWritten = '';
  var lastEvidence = null;
  var coreReady = false;
  var corePending = false;
  var gaPending = false;
  var intercepting = false;
  var resumingCheckout = false;
  var gaAttempts = 0;
  var MAX_GA_ATTEMPTS = 4;
  var readyResolve;
  var readyPromise = new Promise(function (resolve) {
    readyResolve = resolve;
  });

  function clip(value, max) {
    var str = value == null ? '' : String(value);
    if (str.length <= max) return str;
    return str.slice(0, max);
  }

  function shopifyRoot() {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root;
  }

  function cartJsUrl() {
    if (window.routes && window.routes.cart_url) {
      return String(window.routes.cart_url).replace(/\/$/, '') + '.js';
    }
    return shopifyRoot() + 'cart.js';
  }

  function cartUpdateUrl() {
    if (window.routes && window.routes.cart_update_url) {
      return window.routes.cart_update_url;
    }
    return shopifyRoot() + 'cart/update.js';
  }

  function safeHost(href) {
    if (!href) return '';
    try {
      return new URL(href, location.href).hostname.toLowerCase();
    } catch (e) {
      return '';
    }
  }

  function hostMatches(host, suffixes) {
    if (!host) return false;
    for (var i = 0; i < suffixes.length; i++) {
      var suffix = suffixes[i];
      if (host === suffix || host.slice(-(suffix.length + 1)) === '.' + suffix) return true;
    }
    return false;
  }

  function isGoogleHost(host) {
    if (!host) return false;
    if (host === 'com.google.android.googlequicksearchbox') return true;
    if (host.indexOf('google.') !== -1) return /(^|\.)google\./.test(host);
    return host === 'googleusercontent.com' || host.slice(-19) === '.googleusercontent.com';
  }

  function isBingHost(host) {
    return host === 'com.microsoft.bing' || hostMatches(host, ['bing.com', 'bing.co.uk', 'bing.net']);
  }

  function isAiHost(host) {
    return hostMatches(host, AI_HOSTS);
  }

  function isInternalHost(host) {
    if (!host) return false;
    var here = (location.hostname || '').toLowerCase();
    if (host === here) return true;
    if (here && (host === 'www.' + here || here === 'www.' + host)) return true;
    if (hostMatches(host, ['bridgewaterdirect.co.uk'])) return true;
    if (host.indexOf('myshopify.com') !== -1) return true;
    if (host === 'shop.app' || host.indexOf('.shopify.com') !== -1) return true;
    if (host.indexOf('shopifycs.com') !== -1) return true;
    return false;
  }

  function param(params, name) {
    var direct = params.get(name);
    if (direct) return direct;
    var lower = name.toLowerCase();
    var keys = params.keys();
    var next = keys.next();
    while (!next.done) {
      if (String(next.value).toLowerCase() === lower) {
        return params.get(next.value) || '';
      }
      next = keys.next();
    }
    return '';
  }

  function allowlistedQuery(params) {
    var parts = [];
    for (var i = 0; i < QUERY_ALLOWLIST.length; i++) {
      var key = QUERY_ALLOWLIST[i];
      var value = param(params, key);
      if (value) parts.push(key + '=' + encodeURIComponent(value));
    }
    return parts.join('&');
  }

  function compactReferrer(href) {
    if (!href) return '';
    try {
      var url = new URL(href, location.href);
      return clip(url.hostname + (url.pathname || ''), REF_MAX);
    } catch (e) {
      return clip(safeHost(href), REF_MAX);
    }
  }

  function emptyRecord() {
    return {
      channel: CHANNELS.DIRECT,
      source: '(direct)',
      medium: '(none)',
      campaign: '',
      content: '',
      srsltid: '',
      gclid: '',
      gbraid: '',
      wbraid: '',
      landing_path: '',
      landing_query: '',
      referrer: '',
      timestamp: '',
      carried: '',
      carried_at: '',
      version: VERSION,
      ga_client_id: '',
      ga_session_id: ''
    };
  }

  function classify(href, referrerHref) {
    var rec = emptyRecord();
    var url;

    try {
      url = new URL(href);
    } catch (e) {
      rec.timestamp = new Date().toISOString();
      return rec;
    }

    var params = url.searchParams;
    var utmSource = param(params, 'utm_source');
    var utmMedium = param(params, 'utm_medium');
    var utmCampaign = param(params, 'utm_campaign');
    var utmContent = param(params, 'utm_content');
    var refHost = safeHost(referrerHref);
    var sourceLower = utmSource.toLowerCase();
    var mediumLower = utmMedium.toLowerCase();
    var campaignLower = utmCampaign.toLowerCase();
    var contentLower = utmContent.toLowerCase();
    var googleSource = isGoogleHost(refHost) || sourceLower === 'google';
    var bingSource = isBingHost(refHost) || sourceLower === 'bing';
    var paid = !!(param(params, 'gclid') || param(params, 'gbraid') || param(params, 'wbraid'));
    var bingPaid = !!param(params, 'msclkid');
    var gmcUtm =
      mediumLower === 'product_sync' ||
      campaignLower === 'sag_organic' ||
      contentLower === 'sag_organic';
    var srsltid = param(params, 'srsltid');
    var gmcSrsltid = !!srsltid && googleSource;

    rec.gclid = clip(param(params, 'gclid'), ID_MAX);
    rec.gbraid = clip(param(params, 'gbraid'), ID_MAX);
    rec.wbraid = clip(param(params, 'wbraid'), ID_MAX);
    rec.srsltid = clip(srsltid, ID_MAX);
    rec.campaign = clip(utmCampaign, TEXT_MAX);
    rec.content = clip(utmContent, TEXT_MAX);
    rec.landing_path = clip(url.pathname || '/', TEXT_MAX);
    rec.landing_query = clip(allowlistedQuery(params), 800);
    rec.referrer = compactReferrer(referrerHref);
    rec.timestamp = new Date().toISOString();
    rec.version = VERSION;

    if (paid) {
      rec.channel = CHANNELS.PAID;
      rec.source = utmSource || 'google';
      rec.medium = utmMedium || 'cpc';
      return rec;
    }

    if (gmcUtm || gmcSrsltid) {
      rec.channel = CHANNELS.GMC;
      rec.source = utmSource || 'google';
      rec.medium = utmMedium || 'product_sync';
      return rec;
    }

    if (googleSource) {
      rec.channel = CHANNELS.GOOGLE_ORG;
      rec.source = utmSource || 'google';
      rec.medium = utmMedium || 'organic';
      return rec;
    }

    if (bingSource && !bingPaid) {
      rec.channel = CHANNELS.BING_ORG;
      rec.source = utmSource || 'bing';
      rec.medium = utmMedium || 'organic';
      return rec;
    }

    if (isAiHost(refHost)) {
      rec.channel = CHANNELS.AI;
      rec.source = refHost || utmSource || 'ai';
      rec.medium = utmMedium || 'referral';
      return rec;
    }

    if (refHost && !isInternalHost(refHost)) {
      rec.channel = CHANNELS.REFERRAL;
      rec.source = sourceLower ? utmSource : refHost;
      rec.medium = utmMedium || 'referral';
      return rec;
    }

    if (utmSource && !isInternalHost(refHost)) {
      rec.channel = CHANNELS.REFERRAL;
      rec.source = utmSource;
      rec.medium = utmMedium || 'referral';
      return rec;
    }

    rec.channel = CHANNELS.DIRECT;
    rec.source = '(direct)';
    rec.medium = '(none)';
    return rec;
  }

  function buildEvidence(rec, href, referrerHref) {
    var params;
    try {
      params = new URL(href).searchParams;
    } catch (e) {
      params = new URLSearchParams();
    }
    var refHost = safeHost(referrerHref);
    return {
      paid_click_id: !!(rec.gclid || rec.gbraid || rec.wbraid),
      gmc_utm:
        (param(params, 'utm_medium') || '').toLowerCase() === 'product_sync' ||
        (param(params, 'utm_campaign') || '').toLowerCase() === 'sag_organic' ||
        (param(params, 'utm_content') || '').toLowerCase() === 'sag_organic',
      srsltid: !!rec.srsltid,
      google_referrer: isGoogleHost(refHost),
      google_utm_source: (param(params, 'utm_source') || '').toLowerCase() === 'google',
      bing_referrer: isBingHost(refHost),
      referrer_host: refHost,
      landing_path: rec.landing_path
    };
  }

  function storageGet(store, key) {
    try {
      return store.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(store, key, value) {
    try {
      store.setItem(key, value);
      return true;
    } catch (e) {
      return false;
    }
  }

  function storageRemove(store, key) {
    try {
      store.removeItem(key);
    } catch (e) {}
  }

  function clearLegacyStorage() {
    for (var i = 0; i < LEGACY_KEYS.length; i++) {
      storageRemove(sessionStorage, LEGACY_KEYS[i]);
      storageRemove(localStorage, LEGACY_KEYS[i]);
    }
  }

  function isStaleLedger(ledger) {
    if (!ledger || !ledger.saved_at) return true;
    var ts = Date.parse(ledger.saved_at);
    if (!ts) return true;
    return Date.now() - ts > STALE_MS;
  }

  function hydrateRecord(src) {
    var rec = emptyRecord();
    var data = src || {};
    for (var k in rec) {
      if (Object.prototype.hasOwnProperty.call(rec, k) && data[k] != null) {
        rec[k] = String(data[k]);
      }
    }
    return rec;
  }

  function readLedger() {
    var raw = storageGet(sessionStorage, STORAGE_KEY) || storageGet(localStorage, STORAGE_KEY);
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || !parsed.cart_token) return null;
      if (isStaleLedger(parsed) && parsed.cart_token !== cartToken) return null;
      return {
        cart_token: String(parsed.cart_token),
        record: hydrateRecord(parsed.record),
        written: parsed.written ? String(parsed.written) : ''
      };
    } catch (e) {
      return null;
    }
  }

  // Any cart token. Age is judged by the caller from the acquisition timestamp.
  function readStoredRecord() {
    var raw = storageGet(localStorage, STORAGE_KEY) || storageGet(sessionStorage, STORAGE_KEY);
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || !parsed.record) return null;
      return hydrateRecord(parsed.record);
    } catch (e) {
      return null;
    }
  }

  function ageInDays(rec) {
    var ts = Date.parse(rec && rec.timestamp);
    if (!ts) return Infinity;
    return (Date.now() - ts) / (24 * 60 * 60 * 1000);
  }

  function saveLedger() {
    var payload = JSON.stringify({
      version: VERSION,
      cart_token: cartToken || '',
      record: record,
      written: lastWritten || '',
      saved_at: new Date().toISOString()
    });
    storageSet(sessionStorage, STORAGE_KEY, payload);
    storageSet(localStorage, STORAGE_KEY, payload);
  }

  function isMeaningful(rec) {
    return !!(rec && rec.channel && rec.channel !== CHANNELS.DIRECT);
  }

  function mergeGa(target, source) {
    if (!target.ga_client_id && source.ga_client_id) target.ga_client_id = source.ga_client_id;
    if (!target.ga_session_id && source.ga_session_id) target.ga_session_id = source.ga_session_id;
    return target;
  }

  function chooseRecord(existing, incoming) {
    if (!existing) return incoming;

    mergeGa(incoming, existing);
    mergeGa(existing, incoming);

    if (!isMeaningful(incoming)) return existing;
    if (!isMeaningful(existing)) return incoming;

    var newPaid =
      (incoming.gclid && incoming.gclid !== existing.gclid) ||
      (incoming.gbraid && incoming.gbraid !== existing.gbraid) ||
      (incoming.wbraid && incoming.wbraid !== existing.wbraid);

    if (newPaid) return incoming;

    if (incoming.channel === CHANNELS.GMC && existing.channel !== CHANNELS.GMC) {
      return incoming;
    }

    return existing;
  }

  function cookieValue(name) {
    var parts = document.cookie ? document.cookie.split(';') : [];
    for (var i = 0; i < parts.length; i++) {
      var row = parts[i].trim();
      if (row.indexOf(name + '=') === 0) {
        try {
          return decodeURIComponent(row.slice(name.length + 1));
        } catch (e) {
          return row.slice(name.length + 1);
        }
      }
    }
    return '';
  }

  function readGaClientId() {
    var raw = cookieValue('_ga');
    if (!raw) return '';
    var bits = raw.split('.');
    if (bits.length >= 4) return bits.slice(-2).join('.');
    return '';
  }

  function parseGaSessionValue(raw) {
    if (!raw) return '';
    var gs2 = raw.match(/(?:^|[.$])s(\d+)/);
    if (gs2) return gs2[1];
    var bits = raw.split('.');
    if (bits.length >= 3 && /^\d+$/.test(bits[2])) return bits[2];
    return '';
  }

  function readGaSessionId() {
    return parseGaSessionValue(cookieValue(GA_SESSION_COOKIE));
  }

  function attachGaIds(rec) {
    if (!rec) return rec;
    var clientId = readGaClientId();
    var sessionId = readGaSessionId();
    if (clientId && !rec.ga_client_id) rec.ga_client_id = clientId;
    if (sessionId && !rec.ga_session_id) rec.ga_session_id = sessionId;
    return rec;
  }

  function pairsToAttrs(rec, pairs) {
    var attrs = {};
    for (var i = 0; i < pairs.length; i++) {
      var pair = pairs[i];
      attrs[pair[1]] = rec[pair[0]] == null ? '' : String(rec[pair[0]]);
    }
    return attrs;
  }

  function coreAttributes(rec) {
    return pairsToAttrs(rec, CORE_ATTR_KEYS);
  }

  function gaAttributes(rec) {
    return {
      __bw_ga_client_id: rec.ga_client_id || '',
      __bw_ga_session_id: rec.ga_session_id || ''
    };
  }

  function allAttributes(rec) {
    return pairsToAttrs(rec, ATTR_KEYS);
  }

  function postAttributes(attrs) {
    return fetch(cartUpdateUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ attributes: attrs })
    }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res;
    });
  }

  function persistPhaseA() {
    if (!record) return Promise.resolve();
    if (window.Shopify && Shopify.designMode) return Promise.resolve();

    var attrs = coreAttributes(record);
    var gaNow = gaAttributes(record);
    if (gaNow.__bw_ga_client_id) attrs.__bw_ga_client_id = gaNow.__bw_ga_client_id;
    if (gaNow.__bw_ga_session_id) attrs.__bw_ga_session_id = gaNow.__bw_ga_session_id;

    var serialized = JSON.stringify(allAttributes(record));
    if (lastWritten === serialized) return Promise.resolve();

    corePending = true;
    return postAttributes(attrs)
      .then(function () {
        lastWritten = serialized;
        saveLedger();
      })
      .catch(function (err) {
        console.warn('BWAcquisition: cart attribute write failed', err && err.message ? err.message : err);
      })
      .then(function () {
        corePending = false;
      });
  }

  function persistPhaseB() {
    if (!record) return Promise.resolve();
    if (window.Shopify && Shopify.designMode) return Promise.resolve();

    var gaAttrs = gaAttributes(record);
    var serialized = JSON.stringify(allAttributes(record));
    if (lastWritten === serialized) return Promise.resolve();

    gaPending = true;
    return postAttributes(gaAttrs)
      .then(function () {
        lastWritten = serialized;
        saveLedger();
      })
      .catch(function (err) {
        console.warn('BWAcquisition: GA attribute write failed', err && err.message ? err.message : err);
      })
      .then(function () {
        gaPending = false;
      });
  }

  function markCoreReady() {
    coreReady = true;
    corePending = false;
    if (readyResolve) {
      readyResolve();
      readyResolve = null;
    }
  }

  function waitForCore(timeoutMs) {
    return new Promise(function (resolve) {
      var start = Date.now();
      function tick() {
        if ((coreReady && !corePending) || Date.now() - start >= timeoutMs) {
          resolve();
          return;
        }
        setTimeout(tick, 50);
      }
      tick();
    });
  }

  function fetchCart() {
    var request = window.__bwCartPromise
      ? window.__bwCartPromise
      : fetch(cartJsUrl(), {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        }).then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        });

    var settled = Promise.resolve(request).catch(function (err) {
      console.warn('BWAcquisition: cart read failed', err && err.message ? err.message : err);
      return null;
    });

    return Promise.race([
      settled,
      new Promise(function (resolve) {
        setTimeout(function () {
          resolve(null);
        }, CHECKOUT_WAIT_MS);
      })
    ]);
  }

  function eventCheckoutNode(event) {
    var path = event.composedPath ? event.composedPath() : [];
    if (!path.length && event.target) path = [event.target];
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (!node || node.nodeType !== 1 || !node.matches) continue;
      if (node.matches(CHECKOUT_SELECTOR)) return node;
    }
    if (event.target && event.target.closest) {
      return event.target.closest(CHECKOUT_SELECTOR);
    }
    return null;
  }

  function resumeCheckout(node, event) {
    resumingCheckout = true;
    try {
      if (event && event.type === 'submit' && node && node.tagName === 'FORM') {
        HTMLFormElement.prototype.submit.call(node);
        return;
      }
      if (node && typeof node.click === 'function') {
        node.click();
        return;
      }
    } finally {
      resumingCheckout = false;
    }
  }

  function bindCheckoutIntercept() {
    function blockIfNeeded(event) {
      if (resumingCheckout) return;
      var node = event.type === 'submit' ? event.target : eventCheckoutNode(event);
      if (event.type === 'submit') {
        if (!node || !node.querySelector || !node.querySelector(CHECKOUT_SELECTOR)) return;
      } else if (!node) {
        return;
      }
      if (coreReady && !corePending) return;
      if (intercepting) {
        event.preventDefault();
        event.stopPropagation();
        if (event.stopImmediatePropagation) event.stopImmediatePropagation();
        return;
      }

      intercepting = true;
      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();

      waitForCore(CHECKOUT_WAIT_MS).then(function () {
        intercepting = false;
        if (!coreReady) markCoreReady();
        resumeCheckout(node, event);
      });
    }

    document.addEventListener('click', blockIfNeeded, true);
    document.addEventListener('submit', blockIfNeeded, true);
  }

  function maskToken(token) {
    if (!token) return '';
    if (token.length <= 12) return token.slice(0, 4) + '…';
    return token.slice(0, 8) + '…' + token.slice(-4);
  }

  function debugSnapshot() {
    var copy = emptyRecord();
    if (record) {
      for (var k in copy) {
        if (Object.prototype.hasOwnProperty.call(copy, k)) copy[k] = record[k] || '';
      }
    }
    var writtenAttrs = null;
    try {
      writtenAttrs = lastWritten ? JSON.parse(lastWritten) : null;
    } catch (e) {
      writtenAttrs = null;
    }
    return {
      cart_token: maskToken(cartToken),
      pending: !!(!coreReady || corePending || intercepting),
      ga_pending: gaPending,
      acquisition: copy,
      last_payload_written: writtenAttrs,
      ga_client_id: copy.ga_client_id,
      ga_session_id: copy.ga_session_id,
      evidence: lastEvidence,
      version: VERSION
    };
  }

  function scheduleGaRetry() {
    if (gaAttempts >= MAX_GA_ATTEMPTS) return;
    if (record && record.ga_client_id && record.ga_session_id) return;

    var delays = [1000, 3000, 8000];
    var delay = delays[Math.min(gaAttempts, delays.length - 1)];
    gaAttempts += 1;

    setTimeout(function () {
      if (!record) return;
      var before = record.ga_client_id + '|' + record.ga_session_id;
      attachGaIds(record);
      var after = record.ga_client_id + '|' + record.ga_session_id;
      if (before !== after) {
        saveLedger();
        persistPhaseB();
      }
      scheduleGaRetry();
    }, delay);
  }

  function bindToCart(incoming, cart) {
    cartToken = (cart && cart.token) || '';
    var stored = readLedger();
    var sameCart = !!(stored && cartToken && stored.cart_token === cartToken);
    var adoptPending = !!(stored && cartToken && !stored.cart_token && stored.record);

    if (sameCart || adoptPending) {
      record = chooseRecord(stored.record, incoming);
      lastWritten = stored.written || '';
    } else if (!cartToken && stored && isMeaningful(stored.record) && !isMeaningful(incoming)) {
      record = stored.record;
      lastWritten = stored.written || '';
      cartToken = stored.cart_token || '';
    } else {
      var carried = readStoredRecord();
      if (carried && isMeaningful(carried) && ageInDays(carried) < 14) {
        record = chooseRecord(carried, incoming);
        if (record === carried) {
          record.carried = 'true';
          record.carried_at = record.timestamp || '';
        }
      } else {
        record = incoming;
      }
      lastWritten = '';
    }

    attachGaIds(record);
    saveLedger();
  }

  function init() {
    clearLegacyStorage();
    bindCheckoutIntercept();

    var href = window.__bwLandingHref || window.location.href;
    var referrerHref = window.__bwLandingReferrer || document.referrer || '';
    var incoming = classify(href, referrerHref);
    lastEvidence = buildEvidence(incoming, href, referrerHref);

    setTimeout(function () {
      if (!coreReady) markCoreReady();
    }, CHECKOUT_WAIT_MS);

    fetchCart()
      .then(function (cart) {
        bindToCart(incoming, cart);
        return persistPhaseA();
      })
      .then(function () {
        markCoreReady();
        scheduleGaRetry();
      })
      .catch(function (err) {
        console.warn('BWAcquisition: init failed', err && err.message ? err.message : err);
        if (!record) record = incoming;
        markCoreReady();
      });
  }

  window.BWAcquisition = {
    ready: readyPromise,
    debug: function () {
      return debugSnapshot();
    }
  };

  try {
    init();
  } catch (e) {
    console.warn('BWAcquisition: init failed', e && e.message ? e.message : e);
    markCoreReady();
  }
})();
